import * as React from 'react'
import { useMemo } from 'react'
import { Button, Drawer, FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Form, Skeleton, Switch, Tooltip } from 'antd'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc/es5'
import * as randomstring from 'randomstring'
import deepEqual from 'fast-deep-equal'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { SimpleTimeframe } from '@halo/components/simple-timeframe/simple-timeframe'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asDomainSubject } from '@halo/stores/ability.state'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { Rule } from 'antd/es/form'
import { hoursToSeconds, secondsToHours } from '@pushly/aqe/lib/utils'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'

const DEFAULT_PAGEVIEW_ATTRIBUTION_WINDOW_SECONDS = 10800 // 3 hours

export function getPageviewAttributionWindowHours(domain: DomainDto | undefined): number {
    const paw = domain?.engagement_attribution_config?.pageview_attribution_window
    const intervalSeconds = paw?.interval_seconds
    if (intervalSeconds == null) {
        return secondsToHours(DEFAULT_PAGEVIEW_ATTRIBUTION_WINDOW_SECONDS)
    }
    return secondsToHours(intervalSeconds)
}

type DrawerCloseEvent = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>

interface IEngagementAttributionEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e?: DrawerCloseEvent) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

const validatePageviewWindow = async (_rule: Rule, value: { seconds: number; metric: string }) => {
    const hours = secondsToHours(value.seconds)

    if (hours == null || isNaN(hours)) {
        return Promise.reject('Pageview Attribution Window is required and must be a valid number.')
    }

    if (!Number.isInteger(hours)) {
        return Promise.reject('Pageview Attribution Window must be a whole number.')
    }

    if (hours <= 0) {
        return Promise.reject('Pageview Attribution Window must be greater than 0.')
    }

    return Promise.resolve()
}

export const EngagementAttributionEditor: React.FC<IEngagementAttributionEditor> = ({
    domain,
    visible,
    onClose,
    onDomainUpdated,
}) => {
    const domainService = useMemo(() => Container.get(DomainService), [])

    const [form] = Form.useForm()
    // Re-render on any field change so disableSubmit reflects the latest values.
    Form.useWatch([], form)

    const currentHours = getPageviewAttributionWindowHours(domain)

    const computeChangedDomainValues = (): Partial<DomainDto> => {
        const values: Partial<DomainDto> = {}
        const formValues = form.getFieldsValue()

        const intervalSeconds = formValues.pageviewAttributionWindow?.seconds
        const countClick = formValues.countClickAsPageviewWhenMissing ?? false

        const nextConfig = {
            pageview_attribution_window: {
                interval_seconds: intervalSeconds,
                display_metric: RelativeDateDisplayMetric.HOURS,
            },
            count_click_as_pageview_when_missing: countClick,
        }

        if (!deepEqual(nextConfig, domain.engagement_attribution_config)) {
            values.engagement_attribution_config = nextConfig
        }

        return values
    }

    const handleSubmit = async () => {
        try {
            await form.validateFields()

            const values: Partial<DomainDto> = {
                engagement_attribution_config: {
                    pageview_attribution_window: {
                        interval_seconds: form.getFieldValue('pageviewAttributionWindow')?.seconds,
                        display_metric: RelativeDateDisplayMetric.HOURS,
                    },
                    count_click_as_pageview_when_missing:
                        form.getFieldValue('countClickAsPageviewWhenMissing') ?? false,
                },
            }

            const { ok, data } = await domainService.patch(domain.id, values, {
                showLoadingScreen: true,
            })

            if (ok) {
                onDomainUpdated(data)
                onClose()
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const currentValues = computeChangedDomainValues()

    return (
        <Drawer
            title="Update Engagement Attribution Settings"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well mode="ghost" hideHeader={true} footerMode="hidden" padding="none">
                    <Form.Item
                        name="pageviewAttributionWindow"
                        initialValue={{
                            seconds: hoursToSeconds(currentHours),
                            metric: 'hours',
                        }}
                        rules={[
                            {
                                required: true,
                                validator: validatePageviewWindow,
                            },
                        ]}
                        label={
                            <span>
                                Pageview Attribution Window&nbsp;
                                <Tooltip title="The maximum time after a notification click during which pageviews can be attributed to that click.">
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </span>
                        }
                    >
                        <SimpleTimeframe disabledMetrics={['seconds', 'minutes', 'days']} />
                    </Form.Item>
                    <Form.Item
                        className="count-click-as-pageview"
                        name="countClickAsPageviewWhenMissing"
                        valuePropName="checked"
                        initialValue={
                            domain.engagement_attribution_config?.count_click_as_pageview_when_missing ?? false
                        }
                        label={
                            <span>
                                Count Click as Pageview&nbsp;
                                <Tooltip title="When enabled, every notification click is counted as a pageview — even when no pageview event is received after the click. Use this for clients who have not deployed the SDK on every page of their site.">
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Switch />
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IEngagementAttributionSettingsWellProps {
    loading?: boolean
    domain: DomainDto
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const EngagementAttributionSettingsWell: React.FC<IEngagementAttributionSettingsWellProps> = ({
    loading,
    domain,
    onDomainUpdated,
}) => {
    const [showDrawer, setShowDrawer] = React.useState(false)
    const [drawerKey, setDrawerKey] = React.useState<string | undefined>()

    const appState = React.useMemo(() => Container.get(AppState), [])

    const handleDrawerOpen = React.useCallback(() => {
        setShowDrawer(true)
        setDrawerKey(randomstring.generate())
    }, [])

    const handleDrawerClose = React.useCallback(() => {
        setShowDrawer(false)
    }, [])

    const hours = getPageviewAttributionWindowHours(domain)
    const displayValue = hours === 1 ? `${hours} hour` : `${hours} hours`
    const countClickAsPageview = domain?.engagement_attribution_config?.count_click_as_pageview_when_missing ?? false

    const canUpdate = appState.abilityStore.can(AbilityAction.UPDATE, asDomainSubject(domain))

    return (
        <Well
            className="type-3 nested"
            title="Engagement Attribution Settings"
            actions={
                canUpdate && (
                    <Button size="small" onClick={handleDrawerOpen} disabled={loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                )
            }
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading && (
                    <>
                        <Well mode="ghost" hideHeader={true} hideFooter={true}>
                            <FormLayoutRow label="Pageview Attribution Window" value={displayValue} />
                            <FormLayoutRow
                                label="Count Click as Pageview"
                                value={countClickAsPageview ? 'Enabled' : 'Disabled'}
                            />
                        </Well>

                        <EngagementAttributionEditor
                            key={drawerKey}
                            visible={showDrawer}
                            domain={domain}
                            onClose={handleDrawerClose}
                            onDomainUpdated={onDomainUpdated}
                        />
                    </>
                )}
            </Skeleton>
        </Well>
    )
}
